import { LabelColor } from 'src/components/label';

import { FactoringStatus } from 'src/types/cheque';
import { IFactorableSummary, IFactoringCandidate } from 'src/types/cheque-factoring';

import { fCurrencyRupees } from './format-number';

// Single source of truth for factoring-status colors — avoids repeating the
// inline-ternary-per-file pattern the (unrelated) ChequeStatus color mapping
// has in cheque-table-row.tsx / cheque-details.tsx / update-cheque-status.tsx.
export function getFactoringStatusColor(status?: FactoringStatus | null): LabelColor {
  switch (status) {
    case FactoringStatus.HANDED_OVER:
      return 'info';
    case FactoringStatus.ADVANCE_RECEIVED:
      return 'primary';
    case FactoringStatus.CLEARED:
      return 'success';
    case FactoringStatus.RETURNED:
      return 'error';
    case FactoringStatus.SETTLED_WITH_LOLC:
      // Distinguishes "recovered from a bounce" from a clean CLEARED.
      return 'warning';
    default:
      return 'default';
  }
}

// Mirrors the backend's hardcoded LOLC facility limits (src/common/constants/
// factoring.constant.ts in raw-server). Not returned by the API — bump here
// to match if the backend constants ever change.
export const FACTORING_LIMITS = {
  COMPANY_HEADROOM_CENTS: 206_000_000, // LKR 2,060,000
  DEBTOR_HEADROOM_CENTS: 25_750_000, // LKR 257,500
  CANDIDATE_MIN_DAYS: 20,
  CANDIDATE_MAX_DAYS: 45,
};

// "Factorable Now" label. When a debtor's cheques can't all go together the
// total depends on which one is picked, so show min – max instead of a single
// first-fit figure.
export function formatFactorableRange(summary?: IFactorableSummary | null): string {
  if (!summary) return fCurrencyRupees(0);

  const { factorableMinInCents: min, factorableMaxInCents: max, candidateCount } = summary;
  const amount =
    min === max ? fCurrencyRupees(max) : `${fCurrencyRupees(min)} – ${fCurrencyRupees(max)}`;

  return `${amount} (${candidateCount} candidates)`;
}

// Why each picked cheque breaks a factoring constraint, keyed by cheque id.
// Cheques are walked in the order given, with running per-debtor and company
// totals, so the cheque that tips a debtor (or the company) over its limit —
// and any later cheque for the same debtor — is the one flagged. Admins may
// still batch these; non-admins are stopped before they get here.
export function getSelectionViolations(
  selected: IFactoringCandidate[],
  companyHeadroomInCents: number
): Map<string, string[]> {
  const violations = new Map<string, string[]>();
  const debtorRunning = new Map<string, number>();
  let companyRunning = 0;

  selected.forEach((item) => {
    const reasons: string[] = [];

    if (item.outsideCandidateWindow) {
      reasons.push(
        `Cheque date outside the ${FACTORING_LIMITS.CANDIDATE_MIN_DAYS}–${FACTORING_LIMITS.CANDIDATE_MAX_DAYS} day window`
      );
    }

    const key = `${item.bank}::${item.payerAccountNumber}`;
    const debtorTotal = (debtorRunning.get(key) ?? 0) + item.amountInCents;
    debtorRunning.set(key, debtorTotal);
    if (debtorTotal > item.debtorHeadroomInCents) {
      reasons.push(
        `Over the debtor limit by ${fCurrencyRupees(debtorTotal - item.debtorHeadroomInCents)}`
      );
    }

    companyRunning += item.amountInCents;
    if (companyRunning > companyHeadroomInCents) {
      reasons.push(
        `Over the company limit by ${fCurrencyRupees(companyRunning - companyHeadroomInCents)}`
      );
    }

    if (reasons.length > 0) violations.set(item._id, reasons);
  });

  return violations;
}
